import "@/index.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/common";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Reports from "@/pages/Reports";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadProfile from "@/pages/LeadProfile";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import WhatsAppHub from "@/pages/WhatsAppHub";
import HotLeads from "@/pages/HotLeads";
import Projects from "@/pages/Projects";
import Associates from "@/pages/Associates";
import AssociateProfile from "@/pages/AssociateProfile";
import Meetings from "@/pages/Meetings";
import SiteVisits from "@/pages/SiteVisits";
import Deals from "@/pages/Deals";
import Analytics from "@/pages/Analytics";
import AIIntelligence from "@/pages/AIIntelligence";
import AIKnowledge from "@/pages/AIKnowledge";
import PlaygroundReports from "@/pages/PlaygroundReports";
import AIPlayground from "@/pages/AIPlayground";
import DigitalCards from "@/pages/DigitalCards";
import Automations from "@/pages/Automations";
import VisualFlows from "@/pages/VisualFlows";
import Settings from "@/pages/Settings";
import Account from "@/pages/Account";
import Nurturing from "@/pages/Nurturing";
import FollowUps from "@/pages/FollowUps";
import AgentArena from "@/pages/AgentArena";
import AgentDetail from "@/pages/AgentDetail";
import CloudApprovals from "@/pages/CloudApprovals";
import Appearance from "@/pages/Appearance";
import AdminControl from "@/pages/AdminControl";
import Gods from "@/pages/Gods";
import Broadcasts from "@/pages/Broadcasts";
import UserAccounts from "@/pages/UserAccounts";
import ConsentRegistry from "@/pages/ConsentRegistry";
import ConversationLab from "@/pages/ConversationLab";
import BotStudio from "@/pages/BotStudio";
import Competitions from "@/pages/Competitions";
import Expenses from "@/pages/Expenses";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";

const PAGE_HELP = [
  ["/whatsapp/broadcasts", "Broadcasts", "Send approved WhatsApp templates to selected lead lists with pacing, eligibility checks and delivery reporting."],
  ["/whatsapp/inbox", "WhatsApp Inbox", "Read live conversations, reply as a human, enable AI mode and review lead intelligence without leaving the chat."],
  ["/whatsapp", "WhatsApp Command Centre", "Manage campaigns, templates, workflows, broadcasts, API connectivity and WhatsApp performance from one control centre."],
  ["/admin/gods", "God Recipients", "Manage trusted VIP numbers that receive important alerts, human-handoff requests and daily management summaries."],
  ["/admin/users", "User Accounts", "Create Admin and Associate logins, link associate profiles, reset passwords, revoke access and control account status."],
  ["/admin/consents", "Consent Registry", "Review timestamped WhatsApp opt-in, decline and revocation evidence before running campaigns."],
  ["/admin/expenses", "Live Expenses", "Track measured Cloud AI token usage and WhatsApp sends with transparent provider-rate cost calculations."],
  ["/admin", "Admin Control", "Control AI lanes, integrations, operating rules, system health and important administrative actions."],
  ["/cloud-approvals", "Human Review", "Review conversations or decisions that require human judgement and take the appropriate next action."],
  ["/follow-ups", "Follow-ups", "Track scheduled follow-ups, timing, attempts and outcomes while keeping this lane separate from campaigns and nurturing."],
  ["/nurturing", "Nurturing", "See how long-term leads are being educated and warmed, then adjust their nurturing style and progression."],
  ["/ai-agents/", "AI Agent Profile", "Inspect this AI agent's instructions, performance, assignments, learning and operating status."],
  ["/ai-agents", "AI Agents", "Compare AI sales agents, review their performance and control how leads are assigned."],
  ["/leads/", "Lead Profile", "Review the lead's verified details, AI memory, score, pipeline stage, notes and complete activity history."],
  ["/leads", "Leads", "Search, filter, import and manage investor and associate leads from first contact through conversion."],
  ["/hot-leads", "Hot Leads", "Focus on high-intent prospects that need timely human attention, meetings or assignment."],
  ["/projects", "Projects", "Maintain approved RAV project facts, media and availability used by the AI for safe recommendations."],
  ["/associates/", "Associate Profile", "Review an associate's profile, activity, approvals and monthly performance."],
  ["/associates", "Associates", "Manage associate records and approval-controlled contributions separately from investor leads."],
  ["/meetings", "Meetings", "Schedule and track lead meetings, ownership, status and next actions."],
  ["/site-visits", "Site Visits", "Plan and monitor property visits, confirmations, outcomes and follow-up actions."],
  ["/deals", "Deals", "Follow qualified opportunities through negotiation, booking, won or lost outcomes."],
  ["/analytics", "Analytics", "Measure response, conversion, delivery and team performance to identify what needs attention."],
  ["/reports", "Reports", "Generate management-ready operational and sales reports from CRM activity."],
  ["/ai-intelligence", "AI Intelligence", "Inspect scoring, intent, recommendations and the signals used by the CRM brain."],
  ["/conversation-lab", "Conversation Lab", "Audit conversation quality and approve examples or rules used to improve future chats."],
  ["/admin/competitions", "AI Agent Competition", "Schedule measurable challenges, choose rewards and crown a verified AI winner."],
  ["/bot-studio/", "Bot Studio", "Chat privately with an AI agent, practise, inspect blockers and save approved strategy or knowledge."],
  ["/knowledge", "AI Knowledge", "Add approved sales material and project knowledge that AI agents may use in conversations."],
  ["/playground", "AI Playground", "Safely test AI responses, prompts and project knowledge before using them with real leads."],
  ["/digital-cards", "Digital Visiting Cards", "Design and save professional contact cards with WhatsApp QR and live preview."],
  ["/automations", "Automations", "Create and monitor rule-based actions while preventing conflicts between operational lanes."],
  ["/settings", "Settings", "Configure integrations, AI providers, operating preferences and secure system-wide options."],
  ["/appearance", "Appearance", "Customise branding, colours and visual preferences without changing CRM logic."],
  ["/account", "Account", "Review your profile and personal account settings."],
  ["/", "Dashboard", "See the CRM's current health, priorities, lead movement and actions requiring attention."],
];

function ContextHelp() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const styleId = "mb-context-help-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .mb-page-help-launcher{position:fixed;right:22px;bottom:22px;z-index:2147483644;display:flex;align-items:center;gap:8px;height:42px;padding:0 15px;border:1px solid rgba(148,163,184,.3);border-radius:999px;background:rgba(15,23,42,.94);color:#dbeafe;font:650 12px/1 system-ui;box-shadow:0 12px 34px rgba(0,0,0,.32);cursor:pointer;transition:.2s ease;backdrop-filter:blur(14px)}
        .mb-page-help-launcher span{display:grid;place-items:center;width:21px;height:21px;border-radius:50%;background:linear-gradient(145deg,#f59e0b,#fb923c);color:#111827;font:850 13px/1 system-ui}.mb-page-help-launcher:hover{transform:translateY(-2px);border-color:rgba(245,158,11,.55);color:#fff}
        .mb-help-panel{position:fixed;right:22px;bottom:76px;z-index:2147483645;width:min(390px,calc(100vw - 28px));padding:21px;border:1px solid rgba(148,163,184,.24);border-radius:18px;background:linear-gradient(155deg,#101b30,#09111f);color:#e5eefc;box-shadow:0 24px 70px rgba(0,0,0,.56);font-family:system-ui;animation:mbHelpIn .18s ease-out}
        .mb-help-panel strong{display:block;margin:0 32px 7px 0;color:#fff;font-size:18px}.mb-help-panel p{margin:0;color:#aebed7;font-size:13px;line-height:1.55}.mb-help-kicker{margin-bottom:8px;color:#fbbf24;font:700 10px/1.2 system-ui;letter-spacing:.15em;text-transform:uppercase}.mb-help-note{display:block;margin-top:14px;padding-top:12px;border-top:1px solid rgba(148,163,184,.15);color:#6ee7c7;font-size:11px;line-height:1.45}.mb-help-close{position:absolute;right:12px;top:10px;border:0;background:transparent;color:#94a3b8;font-size:20px;cursor:pointer}
        @keyframes mbHelpIn{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:none}}
        @media(max-width:640px){
          .mb-page-help-launcher{right:max(12px,env(safe-area-inset-right));left:auto;bottom:calc(12px + env(safe-area-inset-bottom));width:44px;min-width:44px;max-width:44px;height:44px;padding:0;gap:0;overflow:hidden;white-space:nowrap;justify-content:center;font-size:0;box-sizing:border-box;transform:none!important}
          .mb-page-help-launcher span{width:24px;height:24px;flex:0 0 24px;font-size:14px}
          .mb-help-panel{left:12px;right:12px;bottom:calc(72px + env(safe-area-inset-bottom));width:auto;max-height:calc(100dvh - 110px);overflow-y:auto;padding:18px;border-radius:16px}
        }
      `;
      document.head.appendChild(style);
    }

    document.querySelectorAll(".mb-help-icon,.mb-help-tooltip").forEach((element) => element.remove());
  }, [location.pathname]);

  useEffect(() => {
    const close = (event) => { if (open && !event.target.closest(".mb-help-panel") && !event.target.closest(".mb-page-help-launcher")) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const page = PAGE_HELP.find(([path]) => path === "/" ? location.pathname === "/" : location.pathname === path || location.pathname.startsWith(path.endsWith("/") ? path : path + "/")) || PAGE_HELP[PAGE_HELP.length - 1];
  return (
    <>
      <button className="mb-page-help-launcher" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={`Help for ${page[1]}`}><span>?</span>Help & Guide</button>
      {open && <section className="mb-help-panel" role="dialog" aria-label={`${page[1]} help`}>
        <button className="mb-help-close" onClick={() => setOpen(false)} aria-label="Close help">×</button>
        <div className="mb-help-kicker">You are viewing</div><strong>{page[1]}</strong>
        <p>{page[2]}</p>
        <small className="mb-help-note">Controls remain clean and clickable. This guide automatically changes as you move between CRM modules.</small>
      </section>}
    </>
  );
}

function Protected({ children, adminOnly }) {
  const { user, isAdmin } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#0B1120]"><Spinner /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadProfile />} />
        <Route path="whatsapp" element={<Protected adminOnly><WhatsAppHub /></Protected>} />
        <Route path="whatsapp/inbox" element={<WhatsAppInbox />} />
        <Route path="whatsapp/broadcasts" element={<Protected adminOnly><Broadcasts /></Protected>} />
        <Route path="follow-ups" element={<FollowUps />} />
        <Route path="nurturing" element={<Nurturing />} />
        <Route path="admin" element={<Protected adminOnly><AdminControl /></Protected>} />
        <Route path="admin/gods" element={<Protected adminOnly><Gods /></Protected>} />
        <Route path="admin/users" element={<Protected adminOnly><UserAccounts /></Protected>} />
        <Route path="admin/consents" element={<Protected adminOnly><ConsentRegistry /></Protected>} />
        <Route path="admin/expenses" element={<Protected adminOnly><Expenses /></Protected>} />
        <Route path="cloud-approvals" element={<Protected adminOnly><CloudApprovals /></Protected>} />
        <Route path="ai-agents" element={<Protected adminOnly><AgentArena /></Protected>} />
        <Route path="ai-agents/:id" element={<Protected adminOnly><AgentDetail /></Protected>} />
        <Route path="bot-studio/:id" element={<Protected adminOnly><BotStudio /></Protected>} />
        <Route path="admin/competitions" element={<Protected adminOnly><Competitions /></Protected>} />
        <Route path="hot-leads" element={<Protected adminOnly><HotLeads /></Protected>} />
        <Route path="projects" element={<Projects />} />
        <Route path="associates" element={<Associates />} />
        <Route path="associates/:id" element={<AssociateProfile />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="site-visits" element={<SiteVisits />} />
        <Route path="deals" element={<Deals />} />
        <Route path="analytics" element={<Protected adminOnly><Analytics /></Protected>} />
        <Route path="reports" element={<Protected adminOnly><Reports /></Protected>} />
        <Route path="ai-intelligence" element={<Protected adminOnly><AIIntelligence /></Protected>} />
        <Route path="knowledge" element={<AIKnowledge />} />
        <Route path="conversation-lab" element={<Protected adminOnly><ConversationLab /></Protected>} />
        <Route path="playground" element={<Protected adminOnly><AIPlayground /></Protected>} />
        <Route path="playground/saved" element={<Protected adminOnly><PlaygroundReports /></Protected>} />
        <Route path="playground/saved/:id" element={<Protected adminOnly><PlaygroundReports /></Protected>} />
        <Route path="digital-cards" element={<Protected><DigitalCards /></Protected>} />
        <Route path="automations" element={<Protected adminOnly><Automations /></Protected>} />
        <Route path="flows" element={<Protected adminOnly><VisualFlows /></Protected>} />
        <Route path="settings" element={<Protected adminOnly><Settings /></Protected>} />
        <Route path="appearance" element={<Protected adminOnly><Appearance /></Protected>} />
        <Route path="account" element={<Account />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageErrorBoundary>
          <Toaster position="top-right" theme="dark" richColors />
          <ContextHelp />
          <AppRouter />
        </PageErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
